import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { getSupabase } from "@/lib/hooks/api/utils";
import { validateContactField } from "@/lib/hooks/api/validation/contact-field-validator";

export function useBulkUpdateContactStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ contactIds, status }: { contactIds: string[]; status: string }) => {
      const statusValidation = validateContactField("status", status);
      if (!statusValidation.isValid) {
        const errorMessage = statusValidation.errors.join("; ");
        console.error("Status validation failed:", errorMessage);
        throw new Error(`Validation failed: ${errorMessage}`);
      }

      if (!contactIds || contactIds.length === 0) {
        throw new Error("At least one contact ID is required");
      }

      const supabase = getSupabase();
      const { data, error } = await supabase
        .from("contacts")
        .update({
          status: statusValidation.sanitizedValue,
          updated_at: new Date().toISOString(),
        })
        .in("id", contactIds)
        .select();

      if (error) {
        console.error("Error bulk updating contact status:", error);
        throw new Error(error.message);
      }

      return data;
    },
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: ["contacts"] });
      toast.success(`${data?.length || 0} contacts updated successfully`);
    },
    onError: (error) => {
      toast.error("Failed to update contact status");
      console.error("Error bulk updating contact status:", error);
    },
  });
}

export function useBulkDeleteContacts() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (contactIds: string[]) => {
      const supabase = getSupabase();
      const { data, error } = await supabase
        .from("contacts")
        .delete()
        .in("id", contactIds)
        .select("id");

      if (error) {
        console.error("Error bulk deleting contacts:", error);
        throw new Error(error.message);
      }

      return data;
    },
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: ["contacts"] });
      toast.success(`${data?.length || 0} contacts deleted successfully`);
    },
    onError: (error) => {
      toast.error("Failed to delete contacts");
      console.error("Error bulk deleting contacts:", error);
    },
  });
}

export function useBulkArchiveContacts() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (contactIds: string[]) => {
      if (!contactIds || contactIds.length === 0) {
        throw new Error("At least one contact ID is required");
      }

      const isActiveValidation = validateContactField("is_active", false);
      if (!isActiveValidation.isValid) {
        const errorMessage = isActiveValidation.errors.join("; ");
        console.error("Archive validation failed:", errorMessage);
        throw new Error(`Validation failed: ${errorMessage}`);
      }

      const supabase = getSupabase();
      const { data, error } = await supabase
        .from("contacts")
        .update({
          is_active: false,
          updated_at: new Date().toISOString(),
        })
        .in("id", contactIds)
        .select();

      if (error) {
        console.error("Error bulk archiving contacts:", error);
        throw new Error(error.message);
      }

      return data;
    },
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: ["contacts"] });
      toast.success(`${data?.length || 0} contacts archived successfully`);
    },
    onError: (error) => {
      toast.error("Failed to archive contacts");
      console.error("Error bulk archiving contacts:", error);
    },
  });
}

export function useBulkExportContacts() {
  return useMutation({
    mutationFn: async (contactIds: string[]) => {
      const supabase = getSupabase();
      const { data, error } = await supabase.from("contacts").select("*").in("id", contactIds);

      if (error) {
        console.error("Error fetching contacts for export:", error);
        throw new Error(error.message);
      }

      if (!data || data.length === 0) {
        throw new Error("No contacts found to export");
      }

      const { objectsToCSV, downloadCSV, formatDateForCSV, formatBooleanForCSV } =
        await import("@/lib/utils/csv-export");

      const exportData = data.map((contact) => ({
        ID: contact.id,
        Name: contact.name || "",
        Email: contact.email || "",
        Phone: contact.phone || "",
        "Company Name": contact.company_name || "",
        Status: contact.status || "",
        "Is Active": formatBooleanForCSV(contact.is_active),
        Address: contact.address || "",
        City: contact.city || "",
        State: contact.state || "",
        "Zip Code": contact.zip_code || "",
        Country: contact.country || "",
        Notes: contact.notes || "",
        "Created At": formatDateForCSV(contact.created_at),
        "Updated At": formatDateForCSV(contact.updated_at),
      }));

      const csvContent = objectsToCSV(exportData);
      const timestamp = new Date().toISOString().split("T")[0];
      downloadCSV(csvContent, `contacts_export_${timestamp}.csv`);

      return { exported: data.length };
    },
    onSuccess: (result) => {
      toast.success(`${result.exported} contacts exported successfully`);
    },
    onError: (error) => {
      toast.error("Failed to export contacts");
      console.error("Error exporting contacts:", error);
    },
  });
}
